import React from "react";
import styles from "./style.module.scss";
import logo from "../../../assets/images/mashie-shield-with-text-white.png";
import { Label } from "semantic-ui-react";
import { Link } from "react-router-dom";
import DeprecatedBanner from "./v2/utils/DeprecatedBanner";
import APIVersionDropdown from "./v2/utils/APIVersionDropdown";

const Endpoint = ({ type, location }) => {
    let colour = "green"; // for GET
    switch (type) {
        case "POST":
            colour = "blue";
            break;
        case "PUT":
            colour = "purple";
            break;
        case "PATCH":
            colour = "yellow";
            break;
        case "DELETE":
            colour = "red";
            break;
        default:
            colour = "green";
    }

    return (
        <h3>
            <Label size='large' color={colour}>
                {type}
            </Label>
            <span className={styles.endpoint}>{location}</span>
        </h3>
    );
};

export default function Index() {
    return (
        <div className='app events' style={{ paddingBottom: "100px" }}>
            <div className={styles.fullHeader} alt='event'>
                <img className={styles.entityLogo} src={logo} alt='logo' />
                <h1 className={styles.headerText}>User Documentation (v1)</h1>
            </div>
            <div className='container'>
                <APIVersionDropdown version='v1' />
                <div className='row mt-5'>
                    <div className='col-12'>
                        <h2>About this API</h2>
                        <p>
                            This API allows you to fully manage your members'
                            access to the MASHIE app, including: creating
                            members, updating member information, renewing
                            memberships, deleting members, and releasing members
                            back to MASHIE. You can also see upcoming bookings.
                        </p>
                    </div>
                </div>

                <div className='row mt-5'>
                    <div className='col-12'>
                        <h2>The Member schema</h2>
                        <p>
                            For the sake of readability, we have defined a fully
                            populated Member object below. Going forward, we
                            will use "Member" to represent the below schema in
                            example responses:
                        </p>
                        <pre className={styles.code}>
                            {`{
    "_id": "6512f4ba4895aa31836d1503",
    "createdAt": "2023-09-26T15:11:54.014Z",
    "name": "Anne Example",
    "firstName": "Anne",
    "surname": "Example",
    "email": "anne.example@example.com",
    "emailAddresses": [
        {
            "email": "anne.example@example.com",
            "primary": true,
            "emailType": "Work"
        }
    ],
    "phoneNumbers": [
            {
            "number": "123 456 7890",
            "primary": true,
            "phoneType": "Work"
        }
    ],
    "addresses": [{
        "primary": true,
        "addressType": "Home",
        "line1": "40 Example Street",
        "line2": "Example",
        "town": "Example",
        "county": "Example",
        "postcode": "T35T 123",
        "country": "United Kingdom"
    }],
    "profileImage": "05f9f9d6-a787-469f-a2c9-e2de011557f9.png",
    "eventCreditBalance": 600,
    "teeTimeCreditBalance": 200,
    "membershipExpiresAt": "2025-09-30T23:59:59.999Z",
    "membershipNumber": "MMC147"
}`}
                        </pre>
                    </div>
                </div>

                <div className='row mt-5'>
                    <div className='col-12'>
                        <h2>Endpoints</h2>

                        <Endpoint type='GET' location='/members' />
                        <p>
                            This endpoint returns a paginated list of all
                            members within your entity.
                        </p>
                        <h3>Query parameters</h3>
                        <p>
                            <Label>page</Label>
                            <Label basic className='ml-1'>
                                Int
                            </Label>
                            <span className='ml-3'>
                                Sets the page number for the list of results.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>limit</Label>
                            <Label basic className='ml-1'>
                                Int
                            </Label>
                            <span className='ml-3'>
                                Sets the number of records to return per page.
                                Defaults to 25.
                            </span>
                        </p>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            200
                        </Label>
                        <span className='ml-2'>OK</span>
                        <pre className={styles.code}>
                            {`{ "data": [Member, ...], "total": 147 }`}
                        </pre>
                        <div className={styles.spacer}></div>

                        <Endpoint type='POST' location='/members' />
                        <p>
                            This endpoint creates a new member within your
                            entity.
                        </p>
                        <h3>Request body</h3>
                        <p>
                            <Label>firstName</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>
                                The member's first name.
                            </span>
                        </p>
                        <p className='mt-2'>
                            <Label>surname</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>The member's surname.</span>
                        </p>
                        <p className='mt-2'>
                            <Label>membership</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>
                                The key associated with your entity's membership
                                within MASHIE. This will be provided by your
                                MASHIE rep.
                            </span>
                        </p>
                        <p className='mt-2'>
                            <Label>emailAddresses</Label>
                            <Label basic className='ml-1'>
                                List
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>
                                A list of objects containing email addresses for
                                the member. At least one is required.
                            </span>
                        </p>
                        <div className={styles.indent}>
                            <p>
                                <Label>email</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>The email address.</span>
                            </p>
                            <p className='mt-2'>
                                <Label>primary</Label>
                                <Label basic className='ml-1'>
                                    Boolean
                                </Label>
                                <span className='ml-3'>
                                    Whether this is the member's primary email
                                    address for the MASHIE app. You must set
                                    exactly one primary email address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>emailType</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The type of email address. Must be one of:
                                    Work, Personal, Alternate.
                                </span>
                            </p>
                        </div>
                        <p className='mt-2'>
                            <Label>password</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <span className='ml-3'>
                                The password the member will use this to log
                                into the MASHIE app. Minimum eight characters.
                            </span>
                        </p>
                        <p className='mt-2'>
                            <Label>handicap</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <span className='ml-3'>
                                The handicap the member plays off.
                            </span>
                        </p>
                        <p className='mt-2'>
                            <Label>sendWelcomeEmail</Label>
                            <Label basic className='ml-1'>
                                Boolean
                            </Label>
                            <span className='ml-3'>
                                Whether you want to send the member a welcome
                                email upon creation. Defaults to true.
                            </span>
                        </p>
                        <p className='mt-2'>
                            <Label>phoneNumbers</Label>
                            <Label basic className='ml-1'>
                                List
                            </Label>
                            <span className='ml-3'>
                                A list of objects containing phone numbers for
                                the member.
                            </span>
                        </p>
                        <div className={styles.indent}>
                            <p className='mt-2'>
                                <Label>number</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>The phone number.</span>
                            </p>
                            <p className='mt-2'>
                                <Label>primary</Label>
                                <Label basic className='ml-1'>
                                    Boolean
                                </Label>
                                <span className='ml-3'>
                                    Whether this is the member's primary phone
                                    number.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>phoneType</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The type of phone number. Must be one of:
                                    Mobile, Work, Home.
                                </span>
                            </p>
                        </div>
                        <p className='mt-2'>
                            <Label>addresses</Label>
                            <Label basic className='ml-1'>
                                List
                            </Label>
                            <span className='ml-3'>
                                A list of objects containing addresses for the
                                member.
                            </span>
                        </p>
                        <div className={styles.indent}>
                            <p className='mt-2'>
                                <Label>line1</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The first line of the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>line2</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The second line of the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>town</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The town associated with the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>county</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The county associated with the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>postcode</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The postcode associated with the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>country</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The country associated with the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>primary</Label>
                                <Label basic className='ml-1'>
                                    Boolean
                                </Label>
                                <span className='ml-3'>
                                    Whether this is the member's primary
                                    address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>addressType</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The type of address. Must be one of: Home,
                                    Work, Billing.
                                </span>
                            </p>
                        </div>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            201
                        </Label>
                        <span className='ml-2'>Created</span>
                        <pre className={styles.code}>
                            {`{ "data": Member }`}
                        </pre>
                        <h3>Notes</h3>
                        <p>
                            If no password is provided when creating a member,
                            we will send an email to the member containing a
                            link to set a password.
                        </p>
                        <div className={styles.spacer}></div>

                        <Endpoint type='PATCH' location='/members/:id' />
                        <p>
                            This endpoint updates the member with the associated
                            id.
                        </p>
                        <h3>Path parameters</h3>
                        <p>
                            <Label>id</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>The id of the member.</span>
                        </p>
                        <h3>Request body</h3>
                        <p>
                            <Label>firstName</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <span className='ml-3'>
                                The member's first name.
                            </span>
                        </p>
                        <p className='mt-2'>
                            <Label>surname</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <span className='ml-3'>The member's surname.</span>
                        </p>
                        <p className='mt-2'>
                            <Label>emailAddresses</Label>
                            <Label basic className='ml-1'>
                                List
                            </Label>
                            <span className='ml-3'>
                                A list of objects containing email addresses for
                                the member. At least one is required.
                            </span>
                        </p>
                        <div className={styles.indent}>
                            <p>
                                <Label>email</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>The email address.</span>
                            </p>
                            <p className='mt-2'>
                                <Label>primary</Label>
                                <Label basic className='ml-1'>
                                    Boolean
                                </Label>
                                <span className='ml-3'>
                                    Whether this is the member's primary email
                                    address for the MASHIE app. You must set
                                    exactly one primary email address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>emailType</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The type of email address. Must be one of:
                                    Work, Personal, Alternate.
                                </span>
                            </p>
                        </div>
                        <p className='mt-2'>
                            <Label>handicap</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <span className='ml-3'>
                                The handicap the member plays off.
                            </span>
                        </p>
                        <p className='mt-2'>
                            <Label>phoneNumbers</Label>
                            <Label basic className='ml-1'>
                                List
                            </Label>
                            <span className='ml-3'>
                                A list of objects containing phone numbers for
                                the member.
                            </span>
                        </p>
                        <div className={styles.indent}>
                            <p className='mt-2'>
                                <Label>number</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>The phone number.</span>
                            </p>
                            <p className='mt-2'>
                                <Label>primary</Label>
                                <Label basic className='ml-1'>
                                    Boolean
                                </Label>
                                <span className='ml-3'>
                                    Whether this is the member's primary phone
                                    number.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>phoneType</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The type of phone number. Must be one of:
                                    Mobile, Work, Home.
                                </span>
                            </p>
                        </div>
                        <p className='mt-2'>
                            <Label>addresses</Label>
                            <Label basic className='ml-1'>
                                List
                            </Label>
                            <span className='ml-3'>
                                A list of objects containing addresses for the
                                member.
                            </span>
                        </p>
                        <div className={styles.indent}>
                            <p className='mt-2'>
                                <Label>line1</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The first line of the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>line2</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The second line of the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>town</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The town associated with the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>county</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The county associated with the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>postcode</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The postcode associated with the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>country</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The country associated with the address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>primary</Label>
                                <Label basic className='ml-1'>
                                    Boolean
                                </Label>
                                <span className='ml-3'>
                                    Whether this is the member's primary
                                    address.
                                </span>
                            </p>
                            <p className='mt-2'>
                                <Label>addressType</Label>
                                <Label basic className='ml-1'>
                                    String
                                </Label>
                                <span className='ml-3'>
                                    The type of address. Must be one of: Home,
                                    Work, Billing.
                                </span>
                            </p>
                        </div>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            200
                        </Label>
                        <span className='ml-2'>OK</span>
                        <pre className={styles.code}>
                            {`{ "data": Member }`}
                        </pre>
                        <div className={styles.spacer}></div>

                        <Endpoint type='DELETE' location='/members/:id' />
                        <p>
                            This endpoint deletes the member with the associated
                            id.
                        </p>
                        <h3>Path parameters</h3>
                        <p>
                            <Label>id</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>The id of the member.</span>
                        </p>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            204
                        </Label>
                        <span className='ml-2'>No content</span>
                        <div className={styles.spacer}></div>

                        <Endpoint
                            type='POST'
                            location='/members/:id/membership/renew'
                        />
                        <p>
                            This endpoint renews the membership for the member
                            with the associated id.
                        </p>
                        <h3>Path parameters</h3>
                        <p>
                            <Label>id</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>The id of the member.</span>
                        </p>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            204
                        </Label>
                        <span className='ml-2'>No content</span>
                        <div className={styles.spacer}></div>

                        <Endpoint type='POST' location='/members/:id/release' />
                        <p>
                            This endpoint releases the member with the
                            associated id from your entity and hands them back
                            to MASHIE.
                        </p>
                        <h3>Path parameters</h3>
                        <p>
                            <Label>id</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>The id of the member.</span>
                        </p>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            204
                        </Label>
                        <span className='ml-2'>No content</span>
                        <div className={styles.spacer}></div>

                        <Endpoint
                            type='DELETE'
                            location='/members/:id/membership'
                        />
                        <p>
                            This endpoint cancels the membership for the member
                            with the associated id.
                        </p>
                        <h3>Path parameters</h3>
                        <p>
                            <Label>id</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>The id of the member.</span>
                        </p>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            204
                        </Label>
                        <span className='ml-2'>No content</span>
                        <div className={styles.spacer}></div>

                        <Endpoint
                            type='GET'
                            location='/members/:id/upcoming-bookings'
                        />
                        <p>
                            This endpoint returns a paginated list of all of the
                            passed members bookings.
                        </p>
                        <h3>Path parameters</h3>
                        <p>
                            <Label>id</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>The id of the member.</span>
                        </p>
                        <h3>Query parameters</h3>
                        <p>
                            <Label>page</Label>
                            <Label basic className='ml-1'>
                                Int
                            </Label>
                            <span className='ml-3'>
                                Sets the page number for the list of results.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>limit</Label>
                            <Label basic className='ml-1'>
                                Int
                            </Label>
                            <span className='ml-3'>
                                Sets the number of records to return per page.
                                Defaults to 25.
                            </span>
                        </p>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            200
                        </Label>
                        <span className='ml-2'>OK</span>
                        <pre className={styles.code}>
                            {`{
    "data": [
        "upcoming": [
            {
                "_id": "6849bdd72a7b949aff0d3123",
                "type": "TEETIME",
                "date": "2025-05-01T12:00:00.000Z",
                "time": "10:00 - 11:00",
                "status": "requested",
                "title": "course name at club name"
            },
            {
                "_id": "6849be5de208e6bb759cc992",
                "type": "TEETIME",
                "date": "2025-05-01T12:00:00.000Z",
                "time": "09:37",
                "status": "booked",
                "title": "... at ..."
            },
            ...
        ]
    ], 
    "total": 23 
}`}
                        </pre>
                        <h3>Additional information</h3>
                        <p>
                            <Label>type</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <span className='ml-3'>
                                This will either be TEETIME or EVENT. the _id is
                                universal depending on the type of the upcoming
                                'thing'.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>status</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <ul className='ml-3'>
                                <li>
                                    <strong>requested</strong> - default state
                                    when first made.
                                </li>
                                <li>
                                    <strong>awaiting confirmation</strong> -
                                    Awaiting the customer to confirm the date
                                    and time given to them by MASHIE.
                                </li>
                                <li>
                                    <strong>confirmed</strong> - The customer
                                    has confirmed the time that MASHIE has given
                                    them
                                </li>
                                <li>
                                    <strong>booked</strong> - MASHIE has made
                                    the booking at the club.
                                </li>
                                <li>
                                    <strong>cancellation requested</strong> -
                                    The member needs to cancel the booking.
                                    Please note, if this is done after the
                                    status was 'booked', the member may not
                                    receive a refund for their green fees. They
                                    will not receive a refund for their booking
                                    fees in any circumstances.
                                </li>
                                <li>
                                    <strong>cancelled</strong> - MASHIE has
                                    confirmed the cancellation of the booking.
                                </li>
                            </ul>
                        </p>
                        <p className='mt-1'>
                            <Label>date</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <span className='ml-3'>
                                The date the booking is for. The time portion of
                                this date must be ignored as it is defaulted to
                                midday for timezone compatability.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>time</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <span className='ml-3'>
                                If this is a TEETIME type, this can either be
                                the time block the customer has requested (11:00
                                - 12:00) or the specific tee off time (07:35)
                                given by the club when the booking has been
                                made. If this is EVENT, this will be the start
                                time of the event (08:30).
                            </span>
                        </p>
                        <div className={styles.spacer}></div>
                    </div>
                </div>
            </div>
            <DeprecatedBanner version='v1' />
        </div>
    );
}
